/**
 * Store — the single bridge between the pure engine, the SaveManager, and the
 * renderer. The renderer NEVER calls reducers or storage directly; it goes
 * through here, so persistence + autosave + meta-progression happen in exactly
 * one place.
 *
 * Contract:
 *  - Every run-mutating action returns a fresh RunState (engine is immutable-ish)
 *    which we store, autosave, and broadcast to subscribers.
 *  - Autosave fires after every action at a "safe point" (post card-play,
 *    end-turn, node choice, reward, shop purchase) so a quit/reload resumes the
 *    EXACT state, bit-identical.
 *  - When a run ends (death/victory) we fold the result into meta, persist meta,
 *    and clear the run slot.
 */

import {
  SaveManager,
  newRun,
  chooseMapNode,
  playCard as enginePlayCard,
  endTurn as engineEndTurn,
  takeReward,
  rest as engineRest,
  resolveEvent,
  buyShopCard,
  buyShopProgram,
  buyShopRemove,
  leaveShop,
  recordRunResult,
  recordRunAbandoned,
  getCardDef,
  type RunState,
  type MetaState,
  type CharacterId,
  type CombatEvent,
  type EngineResult,
} from "../engine";

/** UI-only "screen" overlays that aren't a run phase (menus, deck view, etc.). */
export type Overlay =
  | "none"
  | "title"
  | "deck"
  | "settings"
  | "compendium"
  | "results"
  | "map-menu";

type Listener = () => void;

export class Store {
  private sm: SaveManager;
  run: RunState | null = null;
  meta: MetaState;
  overlay: Overlay = "title";
  /** A transient notice (save error, hint) shown in the UI. */
  notice: string | null = null;
  /** Last engine events (for combat feedback / floating numbers). */
  lastEvents: CombatEvent[] = [];
  /** True if a resumable run save exists on disk (for the Continue button). */
  hasSavedRun = false;
  /** Load problem from the saved run, if any (surfaced once on title). */
  loadIssue: string | null = null;

  private listeners = new Set<Listener>();

  constructor(sm = new SaveManager()) {
    this.sm = sm;
    const metaOut = this.sm.loadMeta();
    this.meta = metaOut.meta;
    // Probe for a resumable run WITHOUT making it active yet (Continue does that).
    const runOut = this.sm.loadRun();
    this.hasSavedRun = runOut.run != null;
    if (runOut.status !== "ok" && runOut.status !== "none") {
      this.loadIssue = String(runOut.status);
    }
  }

  // --- subscription -------------------------------------------------------
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  private emit(): void {
    for (const fn of this.listeners) fn();
  }

  /**
   * Force a re-render without mutating state — used for pure UI-state changes
   * (targeting selection, keyboard focus) that screens hold module-locally.
   */
  requestRender(): void {
    this.emit();
  }

  // --- helpers ------------------------------------------------------------
  get settings() {
    return this.meta.settings;
  }

  private autosave(): void {
    if (!this.run) return;
    const ok = this.sm.saveRun(this.run);
    if (!ok) this.notice = "Save failed (storage full?). Progress not written.";
  }

  private persistMeta(): void {
    this.sm.saveMeta(this.meta);
  }

  /** Apply an engine result: store run, broadcast events, autosave, re-render. */
  private apply(res: EngineResult): void {
    this.run = res.state;
    this.lastEvents = res.events;
    this.checkRunEnd();
    this.autosave();
    this.emit();
  }

  // --- run lifecycle ------------------------------------------------------

  /** Is there a resumable saved run on disk? */
  canContinue(): boolean {
    return this.hasSavedRun;
  }

  /** Resume the saved run (Continue). Returns false if none/unreadable. */
  continueRun(): boolean {
    const out = this.sm.loadRun();
    if (!out.run) {
      this.hasSavedRun = false;
      if (out.status !== "none") this.loadIssue = String(out.status);
      this.emit();
      return false;
    }
    this.run = out.run;
    this.overlay = "none";
    this.loadIssue = null;
    this.emit();
    return true;
  }

  /** Start a brand-new run (overwrites any existing run slot). */
  startRun(seed: string, character: CharacterId, heat: number): void {
    this.run = newRun(seed, character, heat);
    this.hasSavedRun = true;
    this.overlay = "none";
    this.lastEvents = [];
    this.notice = null;
    this.autosave();
    this.emit();
  }

  /** Abandon the current run (record + clear), return to title. */
  abandonRun(): void {
    if (this.run && this.run.phase !== "dead" && this.run.phase !== "victory") {
      this.meta = recordRunAbandoned(this.meta);
      this.persistMeta();
    }
    this.sm.clearRun();
    this.run = null;
    this.hasSavedRun = false;
    this.overlay = "title";
    this.emit();
  }

  /** Dismiss the run-over results screen back to the title. */
  closeResults(): void {
    this.run = null;
    this.overlay = "title";
    this.emit();
  }

  /**
   * Detect a run that just ended (dead/victory): fold into meta, persist, and
   * clear the run slot so it doesn't resume a finished run. The run object is
   * kept in memory so the results screen can show its final stats.
   */
  private checkRunEnd(): void {
    const run = this.run;
    if (!run) return;
    if (run.phase === "dead" || run.phase === "victory") {
      const won = run.phase === "victory";
      this.meta = recordRunResult(this.meta, {
        won,
        character: run.character,
        floor: run.floor,
        turns: run.stats.turnsTaken,
        kills: run.stats.enemiesKilled,
        eliteKills: run.stats.elitesKilled,
        archetype: dominantArchetype(run),
      });
      this.persistMeta();
      this.sm.clearRun();
      this.hasSavedRun = false;
      this.overlay = "results";
    }
  }

  // --- map ----------------------------------------------------------------
  chooseNode(nodeId: string): void {
    if (!this.run) return;
    this.apply(chooseMapNode(this.run, nodeId));
  }

  // --- combat -------------------------------------------------------------
  playCard(handIndex: number, targetUid?: number): void {
    if (!this.run) return;
    this.apply(enginePlayCard(this.run, handIndex, targetUid));
  }
  endTurn(): void {
    if (!this.run) return;
    this.apply(engineEndTurn(this.run));
  }

  // --- reward / rest / event ---------------------------------------------
  takeReward(cardDefId: string | null, takeProgram = true): void {
    if (!this.run) return;
    this.apply(takeReward(this.run, cardDefId, takeProgram));
  }
  rest(): void {
    if (!this.run) return;
    this.apply(engineRest(this.run));
  }
  resolveEvent(choiceId: string, removeCardUid?: number): void {
    if (!this.run) return;
    this.apply(resolveEvent(this.run, choiceId, removeCardUid));
  }

  // --- shop ---------------------------------------------------------------
  buyCard(slot: string): void {
    if (!this.run) return;
    this.apply(buyShopCard(this.run, slot));
  }
  buyProgram(slot: string): void {
    if (!this.run) return;
    this.apply(buyShopProgram(this.run, slot));
  }
  buyRemove(cardUid: number): void {
    if (!this.run) return;
    this.apply(buyShopRemove(this.run, cardUid));
  }
  leaveShop(): void {
    if (!this.run) return;
    this.apply(leaveShop(this.run));
  }

  // --- settings -----------------------------------------------------------
  setSetting<K extends keyof MetaState["settings"]>(
    key: K,
    value: MetaState["settings"][K],
  ): void {
    this.meta = {
      ...this.meta,
      settings: { ...this.meta.settings, [key]: value },
    };
    this.persistMeta();
    this.emit();
  }

  /** Wipe meta-progression (settings/unlocks/stats). */
  resetMeta(): void {
    this.meta = this.sm.resetMeta();
    this.emit();
  }

  /** Export the active run as a portable string (null if none). */
  exportRun(): string | null {
    if (!this.run) return null;
    return this.sm.exportRunState(this.run);
  }

  /** Import a run from a string; make it active. Returns success. */
  importRun(encoded: string): boolean {
    const run = this.sm.importRun(encoded);
    if (!run) {
      this.notice = "Import failed: not a valid NULLRUN save.";
      this.emit();
      return false;
    }
    this.run = run;
    this.hasSavedRun = true;
    this.overlay = "none";
    this.emit();
    return true;
  }

  // --- overlay nav --------------------------------------------------------
  open(overlay: Overlay): void {
    this.overlay = overlay;
    this.emit();
  }
  closeOverlay(): void {
    this.overlay = "none";
    this.emit();
  }
  clearNotice(): void {
    if (this.notice) {
      this.notice = null;
      this.emit();
    }
  }
}

/**
 * Compute the dominant archetype tag of a run's deck (for meta win-by-archetype
 * stats). Counts non-starter, non-special card tags; returns the most common.
 */
export function dominantArchetype(run: RunState): string | undefined {
  const counts: Record<string, number> = {};
  for (const c of run.deck) {
    let def;
    try {
      def = getCardDef(c.defId);
    } catch {
      continue;
    }
    if (def.rarity === "starter" || def.rarity === "special") continue;
    for (const tag of def.tags ?? []) {
      counts[tag] = (counts[tag] ?? 0) + 1;
    }
  }
  let best: string | undefined;
  let bestN = 0;
  for (const [tag, n] of Object.entries(counts)) {
    if (n > bestN) {
      bestN = n;
      best = tag;
    }
  }
  return best;
}
